/**
 * Back Navigation System
 *
 * Provides a standard way for bundle apps to register back-button handlers.
 * The pill navigator's back button dispatches a `room:back` custom event.
 * Apps can either:
 *   1. Listen for the `room:back` event on `window`
 *   2. Add `data-back` attribute to a clickable element (auto-discovered)
 *
 * Priority:
 *   1. If any listener calls `event.preventDefault()`, the event is handled
 *   2. If a `[data-back]` element exists in #bundle-container, it gets clicked
 *   3. Fallback: `window.history.back()`
 */

export interface RoomBackEvent extends CustomEvent {
  detail: {
    /** The currently active bundle app id */
    appId: string | null;
  };
}

/**
 * Dispatch a back navigation event.
 * Returns true if a handler consumed the event (preventDefault was called).
 */
export function dispatchBackNavigation(appId: string | null): boolean {
  const event = new CustomEvent('room:back', {
    detail: { appId },
    cancelable: true,
    bubbles: true,
  });

  const consumed = !window.dispatchEvent(event);

  if (consumed) return true;

  // Try data-back attribute discovery
  const container = document.getElementById('bundle-container');
  if (container) {
    const backEl = container.querySelector<HTMLElement>('[data-back]');
    if (backEl) {
      backEl.click();
      return true;
    }
  }

  // Fallback: browser history back
  window.history.back();
  return true;
}

/**
 * React hook helper: register a back handler for the current app.
 * Usage in bundle apps:
 *
 * ```ts
 * import { useBackHandler } from '@room/shared-ui';
 *
 * useBackHandler(() => {
 *   // Navigate to previous screen, close modal, etc.
 *   if (canGoBack) {
 *     goBack();
 *     return true; // consumed
 *   }
 *   return false; // let default behavior happen
 * });
 * ```
 */
export function onBackNavigation(handler: (appId: string | null) => boolean | void): () => void {
  const listener = (e: Event) => {
    const customEvent = e as RoomBackEvent;
    const result = handler(customEvent.detail.appId);
    if (result === true) {
      e.preventDefault();
    }
  };

  window.addEventListener('room:back', listener);
  return () => window.removeEventListener('room:back', listener);
}
